/* eslint-disable import/named */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useVirtualizer } from '@tanstack/react-virtual';
import { isEmpty, sort } from 'ramda';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useOnClickOutside } from 'usehooks-ts';

import { EFlightStatus, ETripEvent, ITripEvent, TTripEvent } from 'detailsv2/structure';
import { useEscAction } from 'hooks/useEscAction';
import { extendEventDaysRangeAction, updateEventDaysRangeAction, updateTripEventAction } from 'store/itinerariesItem';
import { tripEventSelector, tripEventsSelector } from 'store/itinerariesItem/chronologicalDetails/selectors';

import { useChronologicalMode } from '../../functions';
import { useTimelineContext } from '../../TimelineContext';
import { ActionRefs } from '../EventCalendar.types';
import { DEFAULT_PERIOD } from './constansts/drag-and-drop';
import { DragAndDropContext, periodContext } from './contexts';
import { DragTypeEnum } from './contexts/drag-and-drop';
import s from './index.module.css';
import { CalendarDay, DaySlot } from './shared';
import { getUpdatedSelectedEvents } from './shared/DaySlot/EventPlank/utils';
import { DragLayer } from './shared/DragLayer';
import { AvailableSlot, CalendarEvent, EDaySlotType, ICalendarPeriod, Period, TCalendarDay } from './types';
import {
  checkIsDayInPeriod,
  DaysGenerator,
  getEventsFromPeriod,
  getNewSelectedPeriod,
  getUpdatedDaysRange,
  getUpdatedDaysRangeForExtending,
  getUpdatedPeriodFromEventList,
  selectTripEventById,
} from './utils';

const getSlotKey = (
  slot: AvailableSlot,
  options: {
    index: number;
    calendarDay: Pick<TCalendarDay, 'dayNumber'>;
  },
) => {
  const { calendarDay, index } = options;

  if (slot.type === EDaySlotType.event) {
    const eventId = slot.data.id;
    const startDayNumber = slot.data.daysRange.start;
    const endDayNumber = slot.data.daysRange.end;

    if (startDayNumber === calendarDay.dayNumber) {
      return `${calendarDay.dayNumber}-${eventId}-active-move`;
    }

    if (endDayNumber === calendarDay.dayNumber) {
      return `${calendarDay.dayNumber}-${eventId}-active-expand`;
    }

    return `${calendarDay.dayNumber}-${eventId}`;
  }

  return `${calendarDay.dayNumber}-${index}`;
};

function Calendar(props: { actionsRefs: ActionRefs }) {
  const { startDate, organizedBy } = useChronologicalMode();
  const { current: daysGenerator } = useRef(new DaysGenerator());
  const { selectedEvents, setSelectedEvents } = useTimelineContext();
  const [periods, setPeriods] = useState<Period[]>([]);
  const draggingStartDay = useRef<number | null>(null);
  const calendarEvents = useSelector<any, CalendarEvent[]>(tripEventsSelector);
  const [tempCalendarEvents, setTempCalendarEvents] = useState<CalendarEvent[]>([]);
  const [highlightedPeriod, setHighlightedPeriod] = useState<ICalendarPeriod>(DEFAULT_PERIOD);
  const [selectedPeriod, setSelectedPeriod] = useState<ICalendarPeriod>(DEFAULT_PERIOD);
  const dispatch = useDispatch();
  const store = useStore();
  const [availableDayForPlus, setAvailableDayForPlus] = useState<number>(0);
  const footerRef = useRef<HTMLDivElement | null>(null);

  useEscAction(() => setSelectedEvents([]));

  const virtual = useVirtualizer({
    count: periods.length,
    estimateSize: () => 150,
    getScrollElement: () => parentRef.current,
  });
  const parentRef = useRef<HTMLDivElement>(null);
  const rows = virtual.getVirtualItems();
  const days = useMemo(() => Array(DaysGenerator.PERIOD_SIZE).fill(null), []);

  const setDraggingStartDay = (dayNumber: number) => {
    draggingStartDay.current = dayNumber;
  };

  const disableFooter = () => {
    if (footerRef.current) {
      footerRef.current?.classList.add('stickyFooterWrapper-disabled');
    }
  };

  const enableFooter = () => {
    if (footerRef.current) {
      footerRef.current?.classList.remove('stickyFooterWrapper-disabled');
    }
  };

  const updateSelectedEvents = (newEvents: CalendarEvent[]) => {
    const updatedPeriod = getUpdatedPeriodFromEventList(newEvents);
    setSelectedEvents(newEvents);
    setHighlightedPeriod(updatedPeriod);

    if (isEmpty(newEvents)) {
      setSelectedPeriod(updatedPeriod);
    }
  };

  const updateSelectedPeriod = (dayNumber: number) => {
    const newSelectedPeriod = getNewSelectedPeriod(selectedPeriod, dayNumber);
    const newSelectedEvents = getEventsFromPeriod(calendarEvents, newSelectedPeriod, selectedEvents, selectedPeriod);
    updateSelectedEvents(newSelectedEvents);
    setSelectedPeriod(newSelectedPeriod);
  };

  const moveEventByDay = useCallback<
    (eventPayload: Pick<CalendarEvent, 'id'>, calendarDay: Pick<TCalendarDay, 'dayNumber'>) => void
  >(
    (eventPayload, calendarDay) => {
      const calendarEvent = selectTripEventById(eventPayload.id)(store.getState());

      if (!calendarEvent) return;

      const newDaysRange: CalendarEvent['daysRange'] = getUpdatedDaysRange(
        calendarEvent,
        calendarDay.dayNumber,
        draggingStartDay.current,
      );

      if (calendarEvent.type === ETripEvent.Flight && calendarEvent.data.fromFsService) {
        dispatch(
          updateTripEventAction({
            ...calendarEvent,
            data: { ...calendarEvent.data, status: EFlightStatus.Common },
          }),
        );
      }

      dispatch(updateEventDaysRangeAction({ id: calendarEvent.id, daysRange: newDaysRange }));
    },
    [dispatch, store],
  );

  const extendEventByDay = useCallback<
    (eventPayload: Pick<CalendarEvent, 'id'>, calendarDay: Pick<TCalendarDay, 'dayNumber'>) => void
  >(
    (eventPayload, calendarDay) => {
      const calendarEvent = selectTripEventById(eventPayload.id)(store.getState());

      if (!calendarEvent) return;

      const newDaysRange: CalendarEvent['daysRange'] = getUpdatedDaysRangeForExtending(calendarEvent, calendarDay);

      dispatch(extendEventDaysRangeAction({ id: calendarEvent.id, daysRange: newDaysRange }));
    },
    [dispatch, store],
  );

  const slotsWithEvents = useMemo(() => {
    if (tempCalendarEvents.length === 0)
      return daysGenerator.generateSlotsWitnEvents(sort((e1, e2) => e1.positions[0] - e2.positions[0], calendarEvents));

    const eventsWithoutTempEvent = calendarEvents.filter(
      (event) => !tempCalendarEvents.find((temp) => temp.id === event.id),
    );
    const sortedEvents = sort(
      (e1, e2) => e1.positions[0] - e2.positions[0],
      [...eventsWithoutTempEvent, ...tempCalendarEvents],
    );
    const result = daysGenerator.generateSlotsWitnEvents(sortedEvents);
    return result;
  }, [calendarEvents, daysGenerator, tempCalendarEvents]);

  useEffect(() => {
    daysGenerator.assignDates({ startDate });

    setPeriods(daysGenerator.periods);
  }, [daysGenerator, startDate]);

  useEffect(() => {
    footerRef.current = document.querySelector('[data-id="stickyFooterWrapper"]');
  }, []);

  const getDaySlots = useCallback(
    (dayNumber: TCalendarDay['dayNumber']) => {
      const day = slotsWithEvents.find((slotWithEvents) => slotWithEvents.dayNumber === dayNumber);
      if (!day) return [];

      return day.slots;
    },
    [slotsWithEvents],
  );

  const checkIsDayActive = useCallback(
    (dayNumber: TCalendarDay['dayNumber']) => {
      const day = slotsWithEvents.find((slotWithEvents) => slotWithEvents.dayNumber === dayNumber);
      if (!day) return false;

      const hasTempEvent = day.slots.find((slot) => {
        if (slot.type !== EDaySlotType.event) return false;
        return slot.data.temp;
      });

      return hasTempEvent?.data?.temp;
    },
    [slotsWithEvents],
  );

  const checkIsHighlightedDay = useCallback(
    (dayNumber: TCalendarDay['dayNumber']) => checkIsDayInPeriod(dayNumber, highlightedPeriod),
    [highlightedPeriod],
  );

  const cellRenderer = useCallback(
    ({ key, columnIndex, rowIndex }: { key: string; columnIndex: number; rowIndex: number }) => {
      const period = periods[rowIndex];
      const calendarDay = period[columnIndex];

      if (!calendarDay) return null;

      return (
        <div
          key={key}
          className={s['day-container']}
          onMouseEnter={() => setAvailableDayForPlus(calendarDay.dayNumber)}
        >
          <CalendarDay
            active={checkIsDayActive(calendarDay.dayNumber)}
            calendarDay={calendarDay}
            onDrop={moveEventByDay}
            isHighlighted={checkIsHighlightedDay(calendarDay.dayNumber)}
            availableDayForPlus={availableDayForPlus}
            updateSelectedPeriod={updateSelectedPeriod}
            organizedBy={organizedBy}
          >
            <periodContext.Provider value={{ period }}>
              {getDaySlots(calendarDay.dayNumber).map((slot, index) => (
                <DaySlot
                  key={getSlotKey(slot, { index, calendarDay })}
                  slot={slot}
                  calendarDay={calendarDay}
                  period={period || []}
                />
              ))}
            </periodContext.Provider>
          </CalendarDay>
        </div>
      );
    },
    [
      checkIsDayActive,
      getDaySlots,
      moveEventByDay,
      periods,
      checkIsHighlightedDay,
      availableDayForPlus,
      organizedBy,
      selectedPeriod,
      virtual,
    ],
  );

  const onDrop = useCallback<DragAndDropContext.ValueT['onDrop']>(
    (item, calendarDay, type) => {
      setTempCalendarEvents([]);
      enableFooter();

      if (type === DragTypeEnum.extend) {
        extendEventByDay(item, calendarDay);
        return;
      }

      moveEventByDay(item, calendarDay);
      draggingStartDay.current = null;
    },
    [moveEventByDay, extendEventByDay],
  );

  const onMultiDrop = (updatedEvents: CalendarEvent[]) => {
    setTempCalendarEvents([]);
    updatedEvents.forEach((e) => {
      dispatch(updateEventDaysRangeAction({ id: e.id, daysRange: e.daysRange }));
    });
    updateSelectedEvents(updatedEvents);
    setSelectedPeriod(DEFAULT_PERIOD);
    enableFooter();
  };

  const onHover = useCallback<DragAndDropContext.ValueT['onHover']>(
    (item, calendarDay, type) => {
      if (!draggingStartDay.current) {
        draggingStartDay.current = calendarDay.dayNumber;
      }

      const isMultiSelect = selectedEvents.length > 0;
      if (isMultiSelect) {
        const newTempCalendarEvents = getUpdatedSelectedEvents(
          selectedEvents,
          draggingStartDay.current,
          calendarDay.dayNumber,
        );
        setTempCalendarEvents(newTempCalendarEvents);

        let earliestDay = Number.MAX_SAFE_INTEGER;
        let lastestDay = 0;
        newTempCalendarEvents.forEach((e) => {
          earliestDay = Math.min(earliestDay, e.daysRange.start);
          lastestDay = Math.max(lastestDay, e.daysRange.end || e.daysRange.start);
        });
        setHighlightedPeriod({ start: earliestDay, end: lastestDay });

        return;
      }

      const getEvent = (tripEventSelector as (id: ITripEvent<ETripEvent>['id']) => (state: any) => TTripEvent)(item.id);

      const eventFromStore: CalendarEvent = getEvent(store.getState());
      if (!eventFromStore) return;

      if (type === DragTypeEnum.extend) {
        const newDaysRange: CalendarEvent['daysRange'] = getUpdatedDaysRangeForExtending(eventFromStore, calendarDay);

        setTempCalendarEvents([{ ...eventFromStore, temp: true, daysRange: newDaysRange }]);
        draggingStartDay.current = null;

        return;
      }
      const newDaysRange: CalendarEvent['daysRange'] = getUpdatedDaysRange(
        eventFromStore,
        calendarDay.dayNumber,
        draggingStartDay.current,
      );

      setTempCalendarEvents([{ ...eventFromStore, temp: true, daysRange: newDaysRange }]);
    },
    [store, selectedEvents, highlightedPeriod],
  );

  useOnClickOutside([parentRef, props.actionsRefs.btnRef, props.actionsRefs.menuRef], () => {
    if (!isEmpty(selectedEvents)) {
      setSelectedEvents([]);
    }
  });

  return (
    <DragAndDropContext.Provider
      value={{
        onDrop,
        onMultiDrop,
        onHover,
        setTempCalendarEvents,
        setDraggingStartDay,
        selectedEvents,
        updateSelectedEvents,
        tempCalendarEvents,
      }}
    >
      <DndProvider backend={HTML5Backend}>
        <div className={s.container} onDragStart={disableFooter}>
          <div className={s['periods-container']} ref={parentRef}>
            <div className={s['virtual-space']} style={{ height: `${virtual.getTotalSize()}px` }}>
              <div className={s['virtual-platform']} style={{ transform: `translateY(${rows[0]?.start || 0}px)` }}>
                {rows.map((row) => (
                  <div
                    key={row.key}
                    ref={virtual.measureElement}
                    data-index={row.index}
                    className={s['period-container']}
                  >
                    {days.map((_, index) =>
                      cellRenderer({
                        key: `${row.key}-${index}`,
                        columnIndex: index % DaysGenerator.PERIOD_SIZE,
                        rowIndex: row.index,
                      }),
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <DragLayer periods={periods} />
      </DndProvider>
    </DragAndDropContext.Provider>
  );
}

export default Calendar;
